import { Link } from "wouter";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";

// ── AEO/LLMO Buyer's Guide Series definition ─────────────────────────────────
// Single source of truth - update here when new posts are added.
export const AEO_SERIES_POSTS = [
  {
    num: 1,
    slug: "what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026",
    title: "What Does an AEO Agency in the UK Actually Do?",
    shortTitle: "What Does an AEO Agency Do?",
  },
  {
    num: 2,
    slug: "aeo-agency-what-to-hire-for-2026",
    title: "What Should You Hire an AEO Agency For?",
    shortTitle: "What to Hire an AEO Agency For",
  },
  {
    num: 3,
    slug: "aeo-agency-pricing-uk-cost-roi-guide-2026",
    title: "How Much Does AEO Cost? UK Agency Pricing, Tiers, and ROI Guide 2026",
    shortTitle: "AEO Agency Pricing & ROI",
  },
  {
    num: 4,
    slug: "how-to-measure-aeo-results-b2b-analytics-guide-2026",
    title: "How Do You Measure AEO Results? The B2B Marketer's Analytics Guide",
    shortTitle: "How to Measure AEO Results",
  },
  {
    num: 5,
    slug: "aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026",
    title: "AEO Agency vs In-House vs Hybrid: Which Model Fits Your B2B Brand?",
    shortTitle: "Agency vs In-House vs Hybrid",
  },
] as const;

interface AEOSeriesNavProps {
  currentSlug: string;
}

export default function AEOSeriesNav({ currentSlug }: AEOSeriesNavProps) {
  const currentIndex = AEO_SERIES_POSTS.findIndex((p) => p.slug === currentSlug);

  // Not a series post - render nothing
  if (currentIndex === -1) return null;

  const current = AEO_SERIES_POSTS[currentIndex];
  const prev = currentIndex > 0 ? AEO_SERIES_POSTS[currentIndex - 1] : null;
  const next = currentIndex < AEO_SERIES_POSTS.length - 1 ? AEO_SERIES_POSTS[currentIndex + 1] : null;

  return (
    <section
      aria-label="AEO Buyer's Guide series navigation"
      className="mt-16 border-t border-border pt-10"
    >
      {/* Series label */}
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-500">
          The AEO &amp; LLMO Buyer's Guide - Part {current.num} of {AEO_SERIES_POSTS.length}
        </span>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mb-8">
        {AEO_SERIES_POSTS.map((p) => (
          <div
            key={p.num}
            className={`h-1.5 rounded-full transition-all ${
              p.num < current.num
                ? "w-8 bg-amber-500"
                : p.num === current.num
                ? "w-8 bg-amber-400 ring-2 ring-amber-400/30"
                : "w-4 bg-border"
            }`}
          />
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {current.num} of {AEO_SERIES_POSTS.length}
        </span>
      </div>

      {/* Prev / Next cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Previous */}
        {prev ? (
          <Link href={`/blog/${prev.slug}/`}>
            <article className="group flex flex-col gap-1.5 rounded-xl border border-border bg-card hover:border-amber-500/50 p-5 transition-all duration-200 cursor-pointer h-full">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                Previous - Part {prev.num}
              </span>
              <p className="font-serif text-sm md:text-base text-foreground group-hover:text-amber-600 transition-colors leading-snug line-clamp-2">
                {prev.shortTitle}
              </p>
            </article>
          </Link>
        ) : (
          // Placeholder to keep grid balanced when there's no prev
          <div />
        )}

        {/* Next */}
        {next ? (
          <Link href={`/blog/${next.slug}/`}>
            <article className="group flex flex-col gap-1.5 rounded-xl border border-border bg-card hover:border-amber-500/50 p-5 transition-all duration-200 cursor-pointer h-full sm:text-right">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground sm:justify-end">
                Next - Part {next.num}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
              <p className="font-serif text-sm md:text-base text-foreground group-hover:text-amber-600 transition-colors leading-snug line-clamp-2">
                {next.shortTitle}
              </p>
            </article>
          </Link>
        ) : (
          // Last post - show a "series complete" card
          <div className="flex flex-col gap-1.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 sm:text-right">
            <span className="flex items-center gap-1.5 text-xs text-amber-600 sm:justify-end">
              <BookOpen className="w-3.5 h-3.5" />
              Series complete
            </span>
            <Link
              href="/blog/topics/aeo-ai-search/"
              className="font-serif text-sm md:text-base text-amber-600 hover:text-amber-500 transition-colors leading-snug"
            >
              Explore all AEO &amp; AI Search articles →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
